import { useState, useEffect, useCallback, useRef } from 'react';
import { CarouselImage } from '../types';

/**
 * Hook personalizado para manejo del carrusel
 * Convierte la funcionalidad JavaScript original a React hooks
 */
export const useCarousel = (
  images: CarouselImage[],
  autoplay = true,
  interval = 5000,
  pauseOnHover = true
) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Función para ir a una diapositiva específica
  const goToSlide = useCallback((index: number) => {
    if (index < 0) {
      setCurrentIndex(images.length - 1);
    } else if (index >= images.length) {
      setCurrentIndex(0);
    } else {
      setCurrentIndex(index);
    }
  }, [images.length]);

  // Ir a la siguiente diapositiva
  const nextSlide = useCallback(() => {
    goToSlide(currentIndex + 1);
  }, [currentIndex, goToSlide]);

  // Ir a la diapositiva anterior
  const prevSlide = useCallback(() => {
    goToSlide(currentIndex - 1);
  }, [currentIndex, goToSlide]);

  // Pausar el carrusel
  const pause = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  // Reanudar el carrusel
  const play = useCallback(() => {
    if (!autoplay) return;
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(nextSlide, interval);
    setIsPlaying(true);
  }, [autoplay, interval, nextSlide]);

  // Manejar eventos de hover (pausar/reanudar)
  const handleMouseEnter = useCallback(() => {
    if (pauseOnHover) {
      pause();
    }
  }, [pause, pauseOnHover]);

  const handleMouseLeave = useCallback(() => {
    if (pauseOnHover && autoplay) {
      play();
    }
  }, [play, pauseOnHover, autoplay]);

  // Manejar eventos táctiles
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    return e.changedTouches[0].screenX;
  }, []);

  const handleTouchEnd = useCallback((startX: number, e: React.TouchEvent) => {
    const endX = e.changedTouches[0].screenX;
    const diff = startX - endX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
  }, [nextSlide, prevSlide]);

  // Efecto para inicializar y limpiar el carrusel
  useEffect(() => {
    if (autoplay && isPlaying) {
      play();
    }

    // Detectar cambios de visibilidad de la página
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pause();
      } else if (autoplay) {
        play();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoplay, isPlaying, play, pause]);

  // Reiniciar intervalo cuando cambie el índice
  useEffect(() => {
    if (autoplay && isPlaying) {
      play();
    }
  }, [currentIndex, autoplay, isPlaying, play]);

  return {
    currentIndex,
    goToSlide,
    nextSlide,
    prevSlide,
    isPlaying,
    pause,
    play,
    handleMouseEnter,
    handleMouseLeave,
    handleTouchStart,
    handleTouchEnd
  };
};